/* eslint-disable camelcase */
import { getBackVersion } from '../config';
import { TargetPlan } from '../constants/onboarding';
import {
    AccountAuthResponse,
    AccountSnapshot,
    ExtendedAccountSnapshot,
    MfaQrCode,
    PatchAccountArgs,
} from '../models';
import { WorkspaceInviteInstance } from '../models/WorkspaceInvite';
import api from './api';

const create = ({
    name,
    email,
    password,
    phone,
    userType,
    inviteCode,
    targetPlan,
}: {
    name: string;
    email: string;
    password: string;
    phone: string;
    userType: string;
    inviteCode?: string;
    targetPlan?: TargetPlan;
}): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'POST',
        url: `${getBackVersion()}/account`,
        action: 'REGISTER',
        data: {
            name: name,
            email: email,
            phone: phone,
            password: password,
            invite_code: inviteCode,
            target_plan: targetPlan,
            user_type: userType,
        },
    });

const login = (
    username: string,
    password: string,
    targetPlan?: TargetPlan
): Promise<AccountAuthResponse> =>
    api.request<AccountAuthResponse>({
        method: 'GET',
        url: `${getBackVersion(true)}/login`,
        action: 'LOGIN',
        params: { target_plan: targetPlan },
        auth: {
            username,
            password,
        },
    });

const tokenAccess = (token: string): Promise<AccountAuthResponse> =>
    api.request<AccountAuthResponse>({
        method: 'GET',
        url: `${getBackVersion(true)}/token_access/${token}`,
        action: 'TOKEN_ACCESS',
    });

const mfa = (mfaToken: string, code: string): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'GET',
        url: `${getBackVersion(true)}/mfa/${mfaToken}/${code}`,
        action: 'MFA',
    });

const adminLogin = (
    customerEmail: string,
    token: string,
    credential: string
): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'POST',
        url: `${getBackVersion()}/ia`,
        action: 'GOOGLE_AUTHORIZATION',
        data: {
            customer_email: customerEmail,
            token: token,
            credential: credential,
        },
    });

const googleLogin = (
    credential: string,
    expectedEmail?: string,
    inviteCode?: string,
    targetPlan?: TargetPlan,
    userType = 'agent'
): Promise<AccountAuthResponse> =>
    api.request<AccountAuthResponse>({
        method: 'POST',
        url: `${getBackVersion()}/account/google`,
        action: 'GOOGLE_AUTHORIZATION',
        data: {
            credential: credential,
            user_type: userType,
            invite_code: inviteCode,
            target_plan: targetPlan,
            expected_email: expectedEmail,
        },
    });

const getAccount = (): Promise<ExtendedAccountSnapshot> =>
    api.request<ExtendedAccountSnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/account`,
    });

const patch = (args: PatchAccountArgs): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/account`,
        data: {
            cpf_number: args.cpfNumber,
            birthdate: args.birthdate.toISODate(),
            name: args.name,
        },
    });

const setPhone = (phone: string): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/account/phone`,
        data: { phone },
    });

const changePassword = (
    oldPassword: string,
    newPassword: string
): Promise<AccountSnapshot> =>
    api.request<AccountSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/account/password_change`,
        data: {
            old_password: oldPassword,
            new_password: newPassword,
        },
    });

const passwordRecovery = (email: string): Promise<void> =>
    api.request<void>({
        method: 'POST',
        url: `${getBackVersion()}/account/password_reset`,
        action: 'PASSWORD_RECOVERY',
        data: {
            email,
        },
    });

const passwordRecoveryUpdate = (
    password: string,
    token: string
): Promise<void> =>
    api.request<void>({
        method: 'PATCH',
        url: `${getBackVersion()}/account/password_reset`,
        action: 'PASSWORD_RECOVERY_UPDATE',
        data: {
            token,
            password,
        },
    });

const getAllInvites = (): Promise<WorkspaceInviteInstance[]> =>
    api.request<WorkspaceInviteInstance[]>({
        method: 'GET',
        url: `${getBackVersion()}/account/invites`,
    });

const getMfaQrCode = (): Promise<MfaQrCode> =>
    api.request<MfaQrCode>({
        method: 'GET',
        url: `${getBackVersion()}/account/mfa/qrcode`,
    });

export default {
    adminLogin,
    changePassword,
    create,
    getAccount,
    getAllInvites,
    getMfaQrCode,
    googleLogin,
    login,
    mfa,
    passwordRecovery,
    passwordRecoveryUpdate,
    patch,
    setPhone,
    tokenAccess,
};
